import { useCallback, useRef, useState } from "react";
import { useReportApi, unwrapReport } from "../api/reportApi";

const SCORE_SHEET_REPORT_ID = "score.competitor-score-sheet";
// Same cadence Reports.jsx (common-fe) polls a run on: an 1200ms head start,
// then every 3000ms. Unlike that page, this hook also gives up after a
// bound — someone standing at a printer needs a definite "it broke", not an
// indefinite spinner (card #2413 AC4).
const POLL_INITIAL_DELAY_MS = 1200;
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 60000;

// #2413 — "print score sheet" stays on the ride editor: it runs + polls the
// competitor-score-sheet report itself (the same run/poll pair the shared
// Reports page uses, see reportApi.js) instead of navigating to /reports and
// making the operator drive the render by hand.
//
// Popup blockers are the trap (card AC3): a browser allows window.open()
// called synchronously inside a click handler, but blocks one called after
// an await — and the report is necessarily async (run, then poll). So the
// tab opens FIRST, blank, still inside the click's own call stack, holding a
// "generating..." placeholder; the finished artifact is loaded into that
// already-open tab once the poll completes. Deliberately no "noopener" on
// this open — we need the returned window handle back to write the
// placeholder and later set its location, and the eventual target is a
// presigned PDF artifact URL (no script), so the usual reverse-tabnabbing
// risk noopener guards against doesn't apply here.
//
// If the synchronous open didn't stick (returned null/undefined — blocked
// outright, or the environment doesn't support it) or the operator closed
// the placeholder tab while the report was still rendering, this falls back
// to opening a new tab once the artifact is ready — an accepted fallback
// per the card when the synchronous open proves impractical, rather than
// silently doing nothing.
export function usePrintScoreSheet() {
  const reportApi = useReportApi();
  const [busy, setBusy] = useState(false);
  // null | "failed" | "timeout" | "network" — the component maps this to a
  // static-literal i18n key (see RideEditor's PRINT_ERROR_KEYS), never
  // interpolates raw error text.
  const [errorKind, setErrorKind] = useState(null);
  // Guards a stale poll (e.g. a second print click) from clobbering state a
  // newer one already settled.
  const requestRef = useRef(0);

  const print = useCallback(
    ({ showId, rideId, overrideIncompleteJudges = false }) => {
      const requestId = ++requestRef.current;
      setErrorKind(null);
      setBusy(true);

      let printWin = null;
      try {
        printWin = window.open("", "_blank");
      } catch {
        printWin = null;
      }
      if (printWin) {
        try {
          printWin.document.title = "Score sheet";
          printWin.document.write(
            '<body style="font-family:sans-serif;padding:2rem;color:#333">Generating score sheet…</body>',
          );
        } catch {
          // Best-effort placeholder only — a write failure doesn't block the
          // run/poll or the eventual redirect below.
        }
      }

      const settle = (kind) => {
        if (requestRef.current !== requestId) return;
        setBusy(false);
        if (kind) setErrorKind(kind);
      };

      const openArtifact = (url) => {
        if (printWin && !printWin.closed) {
          printWin.location.href = url;
        } else {
          window.open(url, "_blank", "noopener,noreferrer");
        }
      };

      const showFailureInPlaceholder = () => {
        if (printWin && !printWin.closed) {
          try {
            printWin.document.body.innerHTML =
              '<body style="font-family:sans-serif;padding:2rem;color:#b00020">Couldn’t generate the score sheet.</body>';
          } catch {
            // Placeholder tab already navigated away or is inaccessible;
            // the page-level error banner still covers it.
          }
        }
      };

      const deadline = Date.now() + POLL_TIMEOUT_MS;
      const poll = async (runId) => {
        if (requestRef.current !== requestId) return;
        let run;
        try {
          run = unwrapReport(await reportApi.runs.getRun(runId));
        } catch {
          showFailureInPlaceholder();
          settle("network");
          return;
        }
        if (requestRef.current !== requestId) return;
        if (run?.status === "COMPLETE") {
          const url = run.artifactUrls?.pdf || run.artifactUrls?.html;
          if (!url) {
            showFailureInPlaceholder();
            settle("failed");
            return;
          }
          openArtifact(url);
          settle(null);
          return;
        }
        if (run?.status === "FAILED") {
          showFailureInPlaceholder();
          settle("failed");
          return;
        }
        if (Date.now() > deadline) {
          showFailureInPlaceholder();
          settle("timeout");
          return;
        }
        setTimeout(() => poll(runId), POLL_INTERVAL_MS);
      };

      (async () => {
        try {
          const run = unwrapReport(
            await reportApi.reports.createReportRun(SCORE_SHEET_REPORT_ID, {
              params: { showId, rideId, overrideIncompleteJudges },
            }),
          );
          if (requestRef.current !== requestId) return;
          setTimeout(() => poll(run.id), POLL_INITIAL_DELAY_MS);
        } catch {
          showFailureInPlaceholder();
          settle("network");
        }
      })();
    },
    [reportApi],
  );

  return { print, busy, errorKind };
}

export default usePrintScoreSheet;
